import React from "react";
import styled from "styled-components";
import { colors } from "./colors";

/**
 * @typedef {Object} AvatarProps
 * @property {React.ReactNode} [initials] - The fallback, shown when there is no image. The caller
 *   derives it: reducing a name to letters is locale-sensitive and belongs to the app, not here.
 * @property {string} [src] - An image, drawn over the initials when it loads.
 * @property {string} [alt] - Empty by default. The image repeats a name that is already beside it
 *   in every use so far, and a screen reader announcing it twice is worse than not at all. Pass a
 *   description only where the avatar stands alone.
 * @property {number} [size=28] - Box size in px. 28 is the table row's value.
 * @property {number} [radius=8] - Corner radius in px. A squared-off marker rather than a circle,
 *   which is what the app's account and trainer tiles have always been.
 * @property {string} [variant='neutral'] - A key of colors. The brand belongs to the caller: a kit
 *   component cannot know which of the two products it renders inside.
 *
 *   A brand variant takes [950] for the initials over [100] for the fill, not [900]: measured on
 *   primary, [900] on [100] is 3.61:1, under the 4.5:1 WCAG AA asks of text this size, while [950]
 *   is 5.23:1. l3rnTech would pass either way at 8.23 and 10.36:1, so [900] would have been legible
 *   in one product and not the other. Same rule as the table header.
 * @property {string} [className]
 */

const Box = styled.div`
    position: relative;
    flex: 0 0 ${({ $size }) => $size}px;
    width: ${({ $size }) => $size}px;
    height: ${({ $size }) => $size}px;
    border-radius: ${({ $radius }) => $radius}px;
    background: ${({ $variant }) => colors[$variant]?.[100]};
    color: ${({ $variant }) => colors[$variant]?.[$variant === "neutral" ? 700 : 950]};
    font-size: ${({ $size }) => Math.round($size * 0.43)}px;
    font-weight: 700;
    line-height: ${({ $size }) => $size}px;
    letter-spacing: 0.02em;
    text-align: center;
    overflow: hidden;
    /* The initials must not be selectable as text: they are a rendering of the name next to them,
       so a copy of the row would otherwise read "AL Air Liquide". */
    user-select: none;
`;

const Image = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    /* contain, not cover: these are company logos, and cropping one is worse than letterboxing it. */
    object-fit: contain;
    background: ${colors.white};
`;

/**
 * A small square marker for a person or an account: an image if there is one, initials if not.
 *
 * Sized and toned to sit *beside* a name without competing with it. The screen this came from had
 * a 40px tile on a mid-grey fill, which made the least informative element on the row the loudest
 * thing on screen; the first correction made it the faintest, which is not the same as fixing it.
 * 28px with neutral[700] initials on neutral[100] measures 5.27:1 — readable, and quiet.
 *
 * @type {React.FC<AvatarProps>}
 */
const Avatar = ({ initials, src, alt = "", size = 28, radius = 8, variant = "neutral", className }) => (
    <Box className={className} $size={size} $radius={radius} $variant={variant}>
        {initials}
        {src && <Image src={src} alt={alt} />}
    </Box>
);

export default Avatar;
